import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from './SEOHead';
import Nav from './Nav';
import Footer from './Footer';

function AboutUs() {
  const { lang } = useParams();
  const { setLanguage, language } = useLanguage();

  useEffect(() => {
    if (lang && (lang === 'en' || lang === 'ar')) {
      setLanguage(lang);
    }
  }, [lang, setLanguage]);

  const content = {
    en: {
      title: "About Audiosum - AI-Powered Audio Transcription & Summarization",
      description: "Learn about Audiosum's mission to make spoken content accessible. Our team builds powerful AI transcription tools for professionals, researchers, and students worldwide.",
      keywords: "about audiosum, company background, mission statement, AI transcription team, audio technology company",
      
      hero: {
        title: "About",
        subtitle: "Turning every spoken word into accessible knowledge"
      },
      
      sections: {
        background: {
          title: "Company Background",
          content: "Audiosum began with a simple idea: make spoken content as searchable and useful as written text. Built for professionals, researchers, and students, Audiosum brings together powerful AI transcription and an easy, polished user experience. From recorded interviews and lectures to podcasts and conference sessions, Audiosum converts audio and video into accurate, readable text so users can find, cite, and reuse ideas faster."
        },
        mission: {
          title: "Mission Statement",
          content: "Turn every spoken word into accessible knowledge. Audiosum's mission is to remove the friction between listening and learning by delivering fast, reliable, and affordable audio-to-text conversion. We aim to help people capture insights, speed research, and share knowledge across teams and communities."
        },
        values: {
          title: "Core Values",
          items: [
            {
              title: "Accuracy first",
              description: "We prioritize transcription quality so users can trust the text they receive."
            },
            {
              title: "Practicality",
              description: "Tools should save time. Our features focus on real-world workflows for professionals and students."
            },
            {
              title: "Inclusivity",
              description: "Making audio accessible to more people—regardless of hearing ability or language background—is central to what we do."
            },
            {
              title: "Transparency",
              description: "Clear pricing, open documentation, and straightforward support help users get the most from our platform."
            },
            {
              title: "Continuous improvement",
              description: "We're constantly refining models and formats to support emerging file types and needs."
            },
            {
              title: "Innovation",
              description: "We embrace new technologies and methodologies to deliver cutting-edge solutions that stay ahead of industry standards."
            }
          ]
        },
        team: {
          title: "Team Highlights",
          content: "Our team blends machine learning engineers, linguists, and product designers who care about clarity. Engineers fine-tune models for real-world audio—noisy classrooms, multi-speaker meetings, and varied accents. Linguists annotate and improve language handling across domains, while product designers focus on workflows that reduce manual cleanup. Together, this cross-disciplinary team keeps Audiosum fast, precise, and easy to adopt."
        },
        achievements: {
          title: "Key Achievements",
          items: [
            "Industry-leading transcription accuracy across multiple audio and video formats",
            "Wide format support and seamless conversion for professionals and students",
            "Comprehensive online documentation to help users get started and customize outputs",
            "Trusted by researchers, educators, and content creators for reliable results"
          ]
        },
        cta: {
          title: "Ready to Get Started?",
          content: "Ready to turn your audio and video into usable text? Try Audiosum today—upload a file, explore our documentation, and see how simple accurate transcription can be.",
          button: "Try Audiosum Free"
        }
      }
    },
    ar: {
      title: "عن أوديوسام - تحويل الصوت إلى نص بالذكاء الاصطناعي",
      description: "تعرف على مهمة أوديوسام في جعل المحتوى الصوتي قابلاً للوصول. فريقنا يبني أدوات قوية لتحويل الصوت إلى نص باستخدام الذكاء الاصطناعي.",
      keywords: "عن أوديوسام, خلفية الشركة, بيان المهمة, فريق تحويل الصوت, شركة تقنية صوتية",
      
      hero: {
        title: "عن",
        subtitle: "تحويل كل كلمة منطوقة إلى معرفة قابلة للوصول"
      },
      
      sections: {
        background: {
          title: "خلفية الشركة",
          content: "بدأت أوديوسام بفكرة بسيطة: جعل المحتوى المنطوق قابلاً للبحث ومفيداً مثل النص المكتوب. مصممة للمحترفين والباحثين والطلاب، تجمع أوديوسام بين تحويل الصوت إلى نص القوي بالذكاء الاصطناعي وتجربة مستخدم سهلة ومصقولة."
        },
        mission: {
          title: "بيان المهمة",
          content: "تحويل كل كلمة منطوقة إلى معرفة قابلة للوصول. مهمة أوديوسام هي إزالة الاحتكاك بين الاستماع والتعلم من خلال توفير تحويل سريع وموثوق وبأسعار معقولة من الصوت إلى النص."
        },
        values: {
          title: "القيم الأساسية",
          items: [
            {
              title: "الدقة أولاً",
              description: "نعطي الأولوية لجودة التحويل حتى يتمكن المستخدمون من الوثوق بالنص الذي يتلقونه."
            },
            {
              title: "العملية",
              description: "الأدوات يجب أن توفر الوقت. تركز ميزاتنا على سير العمل الحقيقي للمحترفين والطلاب."
            },
            {
              title: "الشمولية",
              description: "جعل الصوت قابلاً للوصول لمزيد من الناس—بغض النظر عن القدرة على السمع أو الخلفية اللغوية—هو أساس ما نقوم به."
            },
            {
              title: "الشفافية",
              description: "الأسعار الواضحة والتوثيق المفتوح والدعم المباشر يساعد المستخدمين على الاستفادة القصوى من منصتنا."
            },
            {
              title: "التحسين المستمر",
              description: "نقوم باستمرار بتحسين النماذج والتنسيقات لدعم أنواع الملفات والاحتياجات الناشئة."
            },
            {
              title: "الابتكار",
              description: "نتبنى التقنيات والمنهجيات الجديدة لتقديم حلول متطورة تظل في طليعة معايير الصناعة."
            }
          ]
        },
        team: {
          title: "أبرز نقاط الفريق",
          content: "يجمع فريقنا بين مهندسي التعلم الآلي واللغويين ومصممي المنتجات الذين يهتمون بالوضوح. المهندسون يضبطون النماذج للصوت الحقيقي—الفصول الدراسية الصاخبة واجتماعات متعددة المتحدثين واللهجات المتنوعة."
        },
        achievements: {
          title: "الإنجازات الرئيسية",
          items: [
            "دقة رائدة في الصناعة في تحويل الصوت عبر تنسيقات متعددة",
            "دعم واسع للتنسيقات وتحويل سلس للمحترفين والطلاب",
            "وثائق شاملة عبر الإنترنت لمساعدة المستخدمين على البدء",
            "موثوق به من قبل الباحثين والمعلمين ومنشئي المحتوى"
          ]
        },
        cta: {
          title: "مستعد للبدء؟",
          content: "مستعد لتحويل الصوت والفيديو إلى نص قابل للاستخدام؟ جرب أوديوسام اليوم—ارفع ملفاً واستكشف وثائقنا وانظر كم هو بسيط التحويل الدقيق.",
          button: "جرب أوديوسام مجاناً"
        }
      }
    }
  };

  const currentContent = content[language] || content.en;

  return (
    <div>
      <SEOHead 
        title={currentContent.title}
        description={currentContent.description}
        keywords={currentContent.keywords}
      />
      <Nav />
      
      {/* Hero Section */}
      <section className="py-5 mt-5" style={{ paddingTop: '100px' }}>
        <div className="container">
          <div className="row justify-content-center text-center">
            <div className="col-lg-8">
              <h1 className="display-4 mb-4" style={{ fontFamily: '"Playfair Display", serif', color: '#0F172A' }}>{currentContent.hero.title}</h1>
              <p className="lead mb-4" style={{ fontFamily: '"Tinos", "Times New Roman", serif', color: '#64748b' }}>{currentContent.hero.subtitle}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-5">
        <div className="container">
          {/* Company Background */}
          <div className="row mb-5">
            <div className="col-lg-10 mx-auto">
              <h2 className="h3 fw-bold mb-4" style={{ color: '#0F172A' }}>{currentContent.sections.background.title}</h2>
              <p className="lead" style={{ lineHeight: '1.8', color: '#475569', fontSize: '1.1rem' }}>{currentContent.sections.background.content}</p>
            </div>
          </div>

          {/* Mission Statement */}
          <div className="row mb-5">
            <div className="col-lg-10 mx-auto">
              <h2 className="h3 fw-bold mb-4" style={{ color: '#0F172A' }}>{currentContent.sections.mission.title}</h2>
              <p className="lead" style={{ lineHeight: '1.8', color: '#475569', fontSize: '1.1rem' }}>{currentContent.sections.mission.content}</p>
            </div>
          </div>

          {/* Core Values */}
          <div className="row mb-5">
            <div className="col-lg-10 mx-auto">
              <h2 className="h3 fw-bold mb-5 text-center" style={{ color: '#0F172A' }}>{currentContent.sections.values.title}</h2>
              <div className="row">
                {currentContent.sections.values.items.map((value, index) => (
                  <div key={index} className="col-md-6 mb-4">
                    <div style={{
                      background: '#FFFFFF',
                      backdropFilter: 'blur(10px)',
                      WebkitBackdropFilter: 'blur(10px)',
                      border: '1px solid #E2E8F0',
                      borderRadius: '16px',
                      padding: '2rem',
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                      position: 'relative',
                      overflow: 'hidden',
                      height: '100%',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-5px)';
                      e.currentTarget.style.boxShadow = '0 8px 32px rgba(6, 149, 255, 0.2)';
                      e.currentTarget.style.borderColor = '#CBD5E1';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 20px rgba(6, 149, 255, 0.1)';
                      e.currentTarget.style.borderColor = 'rgba(6, 149, 255, 0.15)';
                    }}
                    >
                      {/* Decorative gradient blob */}
                      <div style={{
                        position: 'absolute',
                        top: '-50%',
                        right: '-30%',
                        width: '150px',
                        height: '150px',
                        background: 'radial-gradient(circle, rgba(6, 149, 255, 0.1) 0%, transparent 70%)',
                        borderRadius: '50%',
                        filter: 'blur(30px)',
                        zIndex: 0
                      }}></div>
                      
                      <div style={{ position: 'relative', zIndex: 1 }}>
                        <div className="d-flex align-items-start mb-3">
                          <div className="flex-shrink-0 me-3">
                            <div style={{
                              background: 'linear-gradient(135deg, rgba(6, 149, 255, 0.2) 0%, rgba(118, 75, 162, 0.2) 100%)',
                              border: '1px solid rgba(6, 149, 255, 0.3)',
                              borderRadius: '12px',
                              width: '48px',
                              height: '48px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <i className="fas fa-check" style={{ color: '#0695FF', fontSize: '1.2rem' }}></i>
                            </div>
                          </div>
                          <div>
                            <h5 className="fw-bold mb-2" style={{ color: '#0F172A' }}>{value.title}</h5>
                          </div>
                        </div>
                        <p className="mb-0" style={{ color: '#475569', lineHeight: '1.6' }}>{value.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Team Highlights */}
          <div className="row mb-5">
            <div className="col-lg-10 mx-auto">
              <h2 className="h3 fw-bold mb-4" style={{ color: '#0F172A' }}>{currentContent.sections.team.title}</h2>
              <p className="lead" style={{ lineHeight: '1.8', color: '#475569', fontSize: '1.1rem' }}>{currentContent.sections.team.content}</p>
            </div>
          </div>

          {/* Key Achievements */}
          <div className="row mb-5">
            <div className="col-lg-10 mx-auto">
              <h2 className="h3 fw-bold mb-4" style={{ color: '#0F172A' }}>{currentContent.sections.achievements.title}</h2>
              <ul className="list-unstyled">
                {currentContent.sections.achievements.items.map((achievement, index) => (
                  <li key={index} className="mb-3 d-flex align-items-start">
                    <i className="fas fa-star text-warning me-3 mt-1"></i>
                    <span style={{ color: '#475569' }}>{achievement}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Call to Action */}
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <div style={{
                background: '#F8FAFC',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1px solid #E2E8F0',
                borderRadius: '24px',
                padding: '3rem',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.06)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                {/* Decorative gradient blob */}
                <div style={{
                  position: 'absolute',
                  top: '-50%',
                  right: '-20%',
                  width: '300px',
                  height: '300px',
                  background: 'radial-gradient(circle, rgba(6, 149, 255, 0.15) 0%, transparent 70%)',
                  borderRadius: '50%',
                  filter: 'blur(40px)',
                  zIndex: 0
                }}></div>
                <div style={{
                  position: 'absolute',
                  bottom: '-30%',
                  left: '-10%',
                  width: '250px',
                  height: '250px',
                  background: 'radial-gradient(circle, rgba(118, 75, 162, 0.1) 0%, transparent 70%)',
                  borderRadius: '50%',
                  filter: 'blur(40px)',
                  zIndex: 0
                }}></div>
                
                <div style={{ position: 'relative', zIndex: 1 }}>
                  <h2 className="h3 fw-bold mb-4" style={{ color: '#6C3AED' }}>{currentContent.sections.cta.title}</h2>
                  <p className="lead mb-4" style={{ color: '#475569' }}>{currentContent.sections.cta.content}</p>
                  <a 
                    href={`https://audiosum.app/signup?lang=${language}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-lg px-5 py-3"
                    style={{ 
                      fontSize: '1.1rem',
                      borderRadius: '50px',
                      background: 'linear-gradient(135deg, #6C3AED, #0EA5E9)',
                      border: 'none',
                      boxShadow: '0 8px 30px rgba(108, 58, 237, 0.3)',
                      color: 'white',
                      fontWeight: '600',
                      textDecoration: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 12px 40px rgba(6, 149, 255, 0.5)';
                      e.currentTarget.style.background = '#0578D6';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 8px 30px rgba(6, 149, 255, 0.4)';
                      e.currentTarget.style.background = '#0695FF';
                    }}
                  >
                    <i className="fas fa-rocket me-2"></i>
                    {currentContent.sections.cta.button}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default AboutUs;